import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, H1, H3, Lede, Section,
} from "../components/Primitives";
import { GuaranteeLine } from "../components/GuaranteeLine";
import { RING_SIZES } from "../lib/cart";
import { postCustomizeInquiry, apiError } from "../lib/api";
import { useSEO } from "../lib/seo";

const SETTINGS = [
  { value: "ring", label: "Ring", note: "Ring shank in the metal of your choice, sized to you" },
  { value: "pendant", label: "Pendant", note: "Claw or bezel pendant, worn on your own chain or ours" },
];

const METALS = [
  { value: "silver-925", label: "Sterling silver (925)" },
  { value: "panchdhatu", label: "Panchdhatu (five-metal alloy)" },
  { value: "gold-18k", label: "Gold 18k" },
  { value: "gold-22k", label: "Gold 22k" },
  { value: "platinum-950", label: "Platinum 950" },
];

export default function Customize() {
  const [params] = useSearchParams();
  const prefillSku = params.get("sku") || "";
  const prefillSlug = params.get("slug") || "";
  const [form, setForm] = useState({
    setting_kind: "ring",
    metal: "silver-925",
    ring_system: "Indian",
    ring_size: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useSEO({
    title: "Customise your stone — ring or pendant setting inquiry",
    description:
      "Have your certified stone set as a ring or pendant, after you've inspected it loose. Sizing in five systems, jeweller consultation, price quote before any work begins.",
    canonical: "/customize",
    noindex: true,
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await postCustomizeInquiry({
        product_slug: prefillSlug || null,
        sku: prefillSku || null,
        setting_kind: form.setting_kind,
        metal: form.metal,
        ring_system: form.setting_kind === "ring" ? form.ring_system : null,
        ring_size: form.setting_kind === "ring" ? form.ring_size : null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes || null,
      });
      setDone(r);
      toast.success("Sent to a consultant.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Section>
        <Container className="max-w-2xl">
          <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Customise" }]} />
          <p className="eyebrow mt-6">Sent</p>
          <H1 className="mt-3">
            Reference <span className="num">{done.reference}</span>
          </H1>
          <p className="ui mt-4 text-sm leading-[1.7] text-dim">{done.message}</p>
          <Link to="/shop" className="link-u ui mt-6 inline-block text-sm">← Back to the shop</Link>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container className="max-w-3xl">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }, { label: "Customise" }]} />
        <p className="eyebrow mt-6">Setting consultation</p>
        <H1 className="mt-3">Have this stone set as a ring or pendant</H1>
        <Lede className="mt-5">
          We ship every stone loose first, always. Once you've inspected it, our jeweller quotes the
          setting in the metal of your choice and sets it after you've approved the quote. Nothing is
          made until you say so.
        </Lede>

        {(prefillSku || prefillSlug) && (
          <p className="ui mt-4 text-xs text-verify" data-testid="customize-prefill">
            Attached to {prefillSku ? <span className="num">{prefillSku}</span> : "this stone"}
            {prefillSlug ? (
              <> · <Link to={`/product/${prefillSlug}`} className="link-u">see the stone page</Link></>
            ) : null}
          </p>
        )}

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="customize-form">
          <fieldset>
            <legend className="menu-h">Setting</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {SETTINGS.map((s) => (
                <label
                  key={s.value}
                  htmlFor={`kind-${s.value}`}
                  className={`card-s flex cursor-pointer flex-col gap-1 p-5 transition-colors ${
                    form.setting_kind === s.value ? "border-gold" : "hover:border-gold/60"
                  }`}
                  data-testid={`customize-kind-${s.value}`}
                >
                  <input
                    id={`kind-${s.value}`}
                    type="radio"
                    name="setting_kind"
                    value={s.value}
                    checked={form.setting_kind === s.value}
                    onChange={set("setting_kind")}
                    className="sr-only"
                  />
                  <span className="ui text-sm font-medium text-ink">{s.label}</span>
                  <span className="ui text-xs leading-[1.6] text-dim">{s.note}</span>
                  <span className="ui mt-1 text-xs italic text-dim">Photograph example — to be added</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="menu-h">Metal preference</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {METALS.map((m) => (
                <label
                  key={m.value}
                  htmlFor={`metal-${m.value}`}
                  className={`fbox border p-3 transition-colors ${
                    form.metal === m.value ? "border-gold bg-wash" : "border-rule hover:border-gold/60"
                  }`}
                >
                  <input
                    id={`metal-${m.value}`}
                    type="radio"
                    name="metal"
                    value={m.value}
                    checked={form.metal === m.value}
                    onChange={set("metal")}
                    className="sr-only"
                  />
                  <span className="fbox__box" aria-hidden="true"><span className="block h-2 w-2 rounded-full bg-current opacity-90" /></span>
                  <span className="fbox__label">{m.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {form.setting_kind === "ring" && (
            <fieldset data-testid="customize-ring-size">
              <legend className="menu-h">Ring size</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="field-label">Sizing system</span>
                  <select value={form.ring_system} onChange={set("ring_system")} className="field ui">
                    {Object.keys(RING_SIZES).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="field-label">Size</span>
                  <select value={form.ring_size} onChange={set("ring_size")} className="field num">
                    <option value="">Select</option>
                    {RING_SIZES[form.ring_system].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
              </div>
              <p className="ui consent-copy mt-3 text-xs text-dim">
                Every system has its own scale. We list all five so nothing is guessed. If you'd like a
                paper sizer posted, ask in the notes below.
              </p>
            </fieldset>
          )}

          <fieldset>
            <legend className="menu-h">How we reach you</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block sm:col-span-2">
                <span className="field-label">Your name</span>
                <input required value={form.name} onChange={set("name")} className="field" data-testid="customize-name" />
              </label>
              <label className="block">
                <span className="field-label">Email</span>
                <input required type="email" value={form.email} onChange={set("email")} className="field" data-testid="customize-email" />
              </label>
              <label className="block">
                <span className="field-label">Phone (with country code)</span>
                <input required value={form.phone} onChange={set("phone")} className="field num" data-testid="customize-phone" />
              </label>
              <label className="block sm:col-span-2">
                <span className="field-label">Notes (optional)</span>
                <textarea rows={3} value={form.notes} onChange={set("notes")} className="field" data-testid="customize-notes"
                  placeholder="Anything worth knowing — bezel vs claw, hand written, matching chain, budget range." />
              </label>
            </div>
          </fieldset>

          <div>
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="customize-submit">
              {busy ? "Sending…" : "Send inquiry"}
            </button>
            <GuaranteeLine testId="customize-guarantee" className="mt-4">
              Nothing is made until you approve the quote. Full refund on the loose stone if you decide against setting it.
            </GuaranteeLine>
          </div>
        </form>
      </Container>
    </Section>
  );
}
